import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabGroup } from '@angular/material/tabs';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { map, pluck, switchMap, tap } from 'rxjs/operators';

import {
  FtthResourceService,
  Resource,
  resourceListToResourceTypeMap,
  ResSpecificationId
} from '../../../../services/api/ftth-resource/ftth-resource.service';
import { BreadcrumbsService } from '../../../../services/utils/breadcrumbs.service';
import { Parent, ResourceTreeService } from 'src/app/services/utils/resource.tree.service';
import { RESOURCES_LEVEL } from 'src/app/utils/Constants';

@Component({
  selector: 'app-ftth-resource-detail',
  templateUrl: './ftth-resource-detail.component.html',
  styleUrls: ['./ftth-resource-detail.component.scss']
})
export class FtthResourceDetailComponent implements OnInit, OnDestroy {

  @ViewChild(MatTabGroup, { static: false })
  matTabGroup: MatTabGroup;

  resource: Resource;
  lanDetails: any[] = [];  
  namingId: string;
  contains: { [p in ResSpecificationId]?: Resource[]; };
  type: ResSpecificationId;
  cvlanLength = '0';
  resourceTree: Array<Parent>;
  ctoParent: Parent | null = null;

  private subscriptions = new Subscription();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private breadcrumbsService: BreadcrumbsService,
    private ftthResourceService: FtthResourceService,
    private resourceTreeService: ResourceTreeService
  ) {
  }

  ngOnInit() {
    this.initBreadcrumbs();
    this.loadResource(true);
    this.subscriptions.add(this.resourceTreeService.parentSubject.subscribe((res) => {
      this.resourceTree = res;
    }));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  loadResource(resetTab: boolean = false) {
    this.subscriptions.add(
      this.activatedRoute.params
        .pipe(
          pluck('id'),
          tap(id => this.initBreadcrumbs(id)),
          map(id => atob(id)),
          tap(id => this.namingId = id),
          switchMap(id => this.ftthResourceService.getResource(id)),
        )
        .subscribe(resource => {
          this.resource = resource;
          this.contains = resourceListToResourceTypeMap(resource.contains);
          this.type = resource.specificationName;
          const parts = this.resource._links.self.href.split('/');
          this.resource['namingId'] = parts[parts.length - 1];
          if (this.matTabGroup && resetTab) {
            this.matTabGroup.selectedIndex = 0;
          }
          this.loadParentTree();
        }));
  }

  private initBreadcrumbs(resourceId?: string) {
    this.translate.get('DUMMY.key').subscribe((translated: string) => {
      // translations are loaded
      this.sendBreadcrumbs(resourceId);
    });
  }

  private sendBreadcrumbs(resourceId?: string) {
    this.breadcrumbsService.sendBreadcrumbs(
      {
        breadcrumbs: [
          { title: 'Recursos', url: 'provision/resources' },
          { title: 'Recursos FTTH', url: 'provision/resources/ftth/search' },
          { title: 'Recurso', url: `provision/resources/ftth/detail/${resourceId}` }
      ]
      });
  }

  private loadParentTree(): void {
    if (!this.resourceTree || this.resourceTree.length === 0) {
      this.resourceTree = this.resourceTreeService.getParents();
    }
    this.resourceTreeService.clearParents();
    const level = RESOURCES_LEVEL.find(res => res.name === this.resource.resourceType).level;
    if (level > 1) {
      // call to function to get parents of intermediate resources only if it's not OLT (first level)
      this.ftthResourceService.getParents(this.resource).subscribe((resp) => {
        if (resp) {
          resp.resources.forEach(element => {
            const resourceType = FtthResourceService.getResourceType(element.resourceSpec.namingId);
            element.resourceType = resourceType;
            // for ctoPorts and splitters, CTO is shown in another component, because it's not part of parent tree structure
            if (element.resourceType === 'CTO') {
              this.ctoParent = {
                name: element.name,
                namingId: element.namingId,
                level: RESOURCES_LEVEL.find(res => res.name === element.resourceType).level,
                title: element.resourceType
              };
            } else if (element.resourceType === 'OLTNode') { // we don't need this in Onivia network, but for other INOs we should remove this else if
              console.info(element);
            } else {
              this.resourceTreeService.sendParent({
                name: element.name,
                namingId: element.namingId,
                level: RESOURCES_LEVEL.find(res => res.name === element.resourceType).level,
                title: element.resourceType
              });
            }
          });
        }
        this.resourceTreeService.sendParent({
          name: this.resource.name,
          namingId: this.namingId,
          level: RESOURCES_LEVEL.find(res => res.name === this.resource.resourceType).level,
          title: this.resource.resourceType
        });
      });
    } else {
      // we have to recalculate here 1st element of tree, because in resource-search we don't have all data
      this.resourceTreeService.sendParent({
        level: 1,
        name: this.resource.name,
        namingId: this.namingId,
        title: this.resource.resourceType
      });
    }
  }

  getSpecLength(resources?: Resource[]): string {
    if (resources) {
      return `${resources.length}`;
    }

    return '0';
  }

  hasAdministrationTab() {
    const administrationTabTypes: ResSpecificationId[] = ['OLTResSpec', 'OLTCardResSpec', 'OLTPortResSpec', 'CTOResSpec', 'SplitterResSpec', 'CTOPortResSpec'];
    return administrationTabTypes.includes(this.type);
  }

  navigateByTree(resource: Parent): void {
    this.resourceTreeService.removeParents(resource);
    this.ctoParent = null;
    this.router.navigate(['..', btoa(resource.namingId)], { relativeTo: this.activatedRoute });
  }
}

